import { lazy, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import cleanDeep from 'clean-deep';
import { unregister } from './registerServiceWorker';
import store from './store';
import { saveState } from './api/localStorage';
import SnacksProvider from 'containers/SnacksProvider';
import TokenLogin from 'containers/TokenLogin';
import { GraphQLProvider, TokenSender } from '@tymate/sergic';
import Maintenance from 'components/Maintenance';
import ErrorBoundary from 'components/ErrorBoundary';
import IoniIndicator from 'components/IoniIndicator';
import { ENVIRONMENT, GTM_ID, SO_GENERAL_MEETING_WEBAPP_URL } from './constants';
import { ui } from './ui';
import { theme } from './ui/colors';
import 'normalize.css/normalize.css';
import './index.css';
import omit from 'lodash/omit';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getLocale } from 'utils';
import TagManager from 'react-gtm-module';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { setNavigate } from './utils/navigate';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const queryClient = new QueryClient({
   defaultOptions: {
      queries: {
         retry: false,
         refetchOnWindowFocus: false,
      },
   },
});

export const tagManagerArgs = {
   gtmId: GTM_ID,
};

if (Boolean(GTM_ID)) {
   TagManager.initialize(tagManagerArgs);
}

i18n.use(initReactI18next).init({
   fallbackLng: process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'preprod' ? null : 'fr',
   lng: getLocale().name,
   defaultNS: 'shared',
   resources: {
      fr: require('./locales/fr.json'),
      'fr-CA': cleanDeep(require('./locales/fr-ca.json')),
      en: require('./locales/en.json'),
   },
   interpolation: {
      escapeValue: false,
   },
});

const Auth = lazy(() => import('containers/Auth'));
const Debug = lazy(() => import('containers/Debug'));
const App = lazy(() => import('App'));
const Callback = lazy(() => import('containers/Callback'));
const CheckYourEmails = lazy(() => import('components/CheckYourEmails'));
const CheckProfile = lazy(() => import('containers/CheckProfile'));

const Loading = () => (
   <div
      style={{
         width: '100vw',
         height: '100vh',
         backgroundColor: ui('background'),
      }}
   />
);

const NavigateInjector = () => {
   const nav = useNavigate();
   useEffect(() => { setNavigate(nav); }, [nav]);
   return null;
};

store.subscribe(() => {
   const state = store.getState();
   const {
      ui: { selectedPlaceId, hasSkippedCheck, isCommercialBannerCollapsed },
      user,
   } = state;

   saveState({
      user: omit(user, 'error'),
      ui: { selectedPlaceId, hasSkippedCheck, isCommercialBannerCollapsed },
   });
});

createRoot(document.getElementById('app')).render(
   <Suspense fallback={<Loading />}>
      <QueryClientProvider client={queryClient}>
         <GraphQLProvider>
            <ThemeProvider theme={theme}>
               <ErrorBoundary>
                  <Provider store={store}>
                     <SnacksProvider>
                        <TokenSender destination={SO_GENERAL_MEETING_WEBAPP_URL}>
                           <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                              <NavigateInjector />
                              <>
                                 <Routes>
                                    <Route path='/connexion/*' element={<Auth />} />
                                    <Route path='/inscription/*' element={<Auth />} />
                                    <Route path='/mot-de-passe-oublie/*' element={<Auth />} />
                                    <Route path='/callback/:type' element={<Callback />} />
                                    <Route path='/email-envoye' element={<CheckYourEmails />} />
                                    <Route path='/verifier-profil' element={<CheckProfile />} />
                                    <Route path='/token-login' element={<TokenLogin />} />
                                    <Route path='/maintenance' element={<Maintenance />} />
                                    <Route path='*' element={<App />} />
                                 </Routes>
                                 {ENVIRONMENT === 'development' && <Debug />}
                                 <IoniIndicator />
                                 <Toaster position='bottom-center' containerStyle={{ zIndex: 2147483647 }} />
                              </>
                           </BrowserRouter>
                        </TokenSender>
                     </SnacksProvider>
                  </Provider>
               </ErrorBoundary>
            </ThemeProvider>
         </GraphQLProvider>
      </QueryClientProvider>
   </Suspense>
);

unregister();
